import { TreeSelect as AntTreeSelect } from 'antd';
import { useRef } from 'react';
import styled from 'styled-components';

import { colours } from 'utils/theme';
import { toExcluded } from 'utils/search';

import './TreeSelect.css';
import IncludeExclude from './IncludeExclude';

const { TreeNode } = AntTreeSelect;

const AntTreeSelectContainer = styled(AntTreeSelect)`
  .ant-select-tree-list-holder {
    overscroll-behavior: contain;
  }
`;

const getTree = treeData => {
  const map = {};

  function traverse(items, parentValues = []) {
    items.forEach(item => {
      const { value, children } = item;
      map[value] = {
        value: value,
        sameLevel: items.filter(i => i.value !== value).map(i => i.value),
        parents: [...parentValues].reverse(),
        children: children ? children.map(child => child.value) : [],
      };
      if (children) {
        traverse(children, [...parentValues, value]);
      }
    });
  }

  traverse(treeData);
  return map;
};

const TreeSelectIncludeExclude = ({
  treeData,
  'data-testid': dataTestId,
  onCreateNewItem,
  buttonText,
  antSelectRef,
  selectedValues,
  resetScrollPositionOnHide,
  onDropdownVisibleChange,
  onChange,
  virtual = false,
  ...rest
}) => {
  const tree = useRef(getTree(treeData));
  const refSelect = useRef();

  const titleDivStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  };

  const isFirefox = () => {
    return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  };

  const selectedParent = value => {
    if (!tree.current[value]) return null;
    const parents = tree.current[value].parents;
    for (let i = 0; i < parents.length; i++) {
      if (selectedValues.includes(parents[i])) {
        return parents[i];
      }
    }
    return null;
  };

  const deselectedParent = value => {
    if (!tree.current[value]) return null;
    const parents = tree.current[value].parents;
    if (!parents) return null;
    for (let i = 0; i < parents.length; i++) {
      if (selectedValues.includes(toExcluded(parents[i]))) {
        return parents[i];
      }
    }
    return null;
  };

  const deselectChildren = (item, values) => {
    for (let i = 0; i < item.children.length; i++) {
      const child = item.children[i];
      values = values.filter(x => x !== child && x !== toExcluded(child));
      values = deselectChildren(tree.current[child], values);
    }
    return values;
  };

  const handleChange = values => {
    onChange(values);
  };

  const handleInclude = (value, isIncluded) => {
    let values = [...selectedValues];
    if (!isIncluded) {
      values = values.filter(x => x !== toExcluded(value));
      const parentSelected = selectedParent(value);
      if (!parentSelected) {
        values.push(value);
      }

      let parentDeselected = deselectedParent(value);
      if (parentDeselected) {
        values = values.filter(x => x !== toExcluded(parentDeselected));
        const parentChilden = tree.current[parentDeselected].children;
        parentChilden
          .filter(x => x !== value && !tree.current[value].parents.includes(x))
          .forEach(x => values.push(toExcluded(x)));

        for (let i = 0; i < tree.current[value].sameLevel.length; i++) {
          const item = tree.current[value].sameLevel[i];
          values.push(toExcluded(item));
        }
      }
      values = deselectChildren(tree.current[value], values);
    } else {
      if (values.some(x => x === value)) {
        values = values.filter(x => x !== value);
        handleChange(values);
        return;
      }

      const parentDeselected = selectedParent(value);
      values = values.filter(x => x !== parentDeselected);

      const leafsToDeselect = [...tree.current[value].parents];
      leafsToDeselect.unshift(value);
      for (let i = 0; i < leafsToDeselect.length; i++) {
        const sameLevelNodes = tree.current[leafsToDeselect[i]].sameLevel;
        for (let m = 0; m < sameLevelNodes.length; m++) {
          const item = sameLevelNodes[m];
          if (item !== value) {
            values.push(item);
          }
        }
        if (tree.current[value].parents[i] === parentDeselected) {
          break;
        }
      }
    }

    handleChange(values);
  };

  const handleExclude = (value, isExcluded) => {
    let values = [...selectedValues];
    if (!isExcluded) {
      values = values.filter(x => x !== value);

      const deselectParents = item => {
        const sameLevel = item.sameLevel;
        const sameLevelAllDeselected = sameLevel.every(x =>
          selectedValues.includes(toExcluded(x))
        );
        if (item.parents.length && sameLevelAllDeselected) {
          const itemsToRemove = [...sameLevel, item.value];
          values = values.filter(
            x => !itemsToRemove.some(y => x === toExcluded(y))
          );
          const parent = tree.current[item.value].parents[0];
          values.push(toExcluded(parent));
          deselectParents(tree.current[parent]);
          return true;
        }
        return false;
      };

      const parentSelected = selectedParent(value);

      if (parentSelected) {
        values = values.filter(x => x !== parentSelected);
        const leafsNotChange = [value, ...tree.current[value].parents];

        const selectChildren = item => {
          for (let i = 0; i < item.children.length; i++) {
            const child = item.children[i];
            if (!leafsNotChange.includes(child)) {
              values.push(child);
            } else {
              selectChildren(tree.current[child]);
            }
          }
        };
        selectChildren(tree.current[parentSelected]);
      }
      if (!deselectParents(tree.current[value])) {
        values.push(toExcluded(value));
      }

      const deselectChildren = item => {
        for (let i = 0; i < item.children.length; i++) {
          const child = item.children[i];
          values = values.filter(x => x !== child && x !== toExcluded(child));
          deselectChildren(tree.current[child]);
        }
      };
      deselectChildren(tree.current[value]);
    } else {
      if (values.some(x => x === toExcluded(value))) {
        values = values.filter(x => x !== toExcluded(value));
        handleChange(values);

        return;
      }

      const parentDeselected = deselectedParent(value);
      values = values.filter(x => x !== toExcluded(parentDeselected));

      const leafsToDeselect = [...tree.current[value].parents];
      leafsToDeselect.unshift(value);
      for (let i = 0; i < leafsToDeselect.length; i++) {
        const sameLevelNodes = tree.current[leafsToDeselect[i]].sameLevel;
        for (let m = 0; m < sameLevelNodes.length; m++) {
          const item = sameLevelNodes[m];
          if (item !== value) {
            values.push(toExcluded(item));
          }
        }
        if (tree.current[value].parents[i] === parentDeselected) {
          break;
        }
      }
    }

    handleChange(values);
  };

  const getTitleElement = (item, isSelected, isExcluded) => {
    const isParentTitleRegex = /(.*)\((\d+)\)(.*)/;
    const match = item.title.match(isParentTitleRegex);
    const title = match ? match[1] : item.title;
    const numChildren = match ? match[2] : null;
    return (
      <div
        style={titleDivStyle}
        onClick={e => {
          handleInclude(item.value, isSelected);
          e.stopPropagation();
        }}
      >
        <div style={{ fontSize: '14px', flex: 1 }}>
          {title}
          {numChildren && <span style={{ opacity: 0.5 }}>({numChildren})</span>}
        </div>
        <IncludeExclude
          isSelected={isSelected}
          isExcluded={isExcluded}
          onExclude={e => {
            handleExclude(item.value, isExcluded);
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      </div>
    );
  };

  const renderTreeNodes = data =>
    data.map(item => {
      const isSelected = selectedValues
        ? selectedValues.includes(item.key) || !!selectedParent(item.key)
        : false;
      const isExcluded =
        selectedValues?.includes(toExcluded(item.key)) ||
        !!deselectedParent(item.key);
      const titleElement = getTitleElement(item, isSelected, isExcluded);

      const backgroundColor = isSelected
        ? colours.inlcudeFilter
        : isExcluded
        ? colours.excludeFilter
        : '';
      return item.children ? (
        <TreeNode
          title={titleElement}
          key={item.key}
          value={item.value}
          style={{ backgroundColor }}
        >
          {renderTreeNodes(item.children)}
        </TreeNode>
      ) : (
        <TreeNode
          title={titleElement}
          key={item.key}
          value={item.value}
          style={{ backgroundColor }}
        />
      );
    });

  const handleDropdownVisibleChange = visible => {
    if (resetScrollPositionOnHide && visible && refSelect.current) {
      requestAnimationFrame(() => refSelect.current.scrollTo({ index: 0 }));
    }
    onDropdownVisibleChange?.(visible);
  };

  return (
    <AntTreeSelectContainer
      {...rest}
      value={selectedValues}
      ref={refSelect}
      showCheckedStrategy={AntTreeSelect.SHOW_PARENT}
      onChange={onChange}
      data-testid={dataTestId}
      virtual={virtual && !isFirefox()}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      getPopupContainer={triggerNode => triggerNode.parentElement}
    >
      {renderTreeNodes(treeData)}
    </AntTreeSelectContainer>
  );
};

export default TreeSelectIncludeExclude;
